import { supabase } from '@/lib/supabase';
import { Purchases } from '@revenuecat/purchases-capacitor';
import type { CustomerInfo } from '@revenuecat/purchases-capacitor';
import { Capacitor } from '@capacitor/core';
import FacebookAnalytics from './facebookAnalytics';

// Constantes
const REVENUECAT_API_KEY_ANDROID = import.meta.env.VITE_REVENUECAT_API_KEY_ANDROID || '';
const REVENUECAT_API_KEY_IOS = import.meta.env.VITE_REVENUECAT_API_KEY_IOS || '';

export interface RevenueCatProduct {
  // Identificador do produto (ex.: 1mensal:mensalbasico)
  id: string;
  // Identificador do pacote dentro do offering (ex.: monthly, annual)
  identifier?: string;
  // Título/descrição
  title: string;
  description: string;
  // Preço formatado localizado (ex.: R$ 9,90)
  price: string;
  // Preço numérico para cálculos (ex.: 9.9)
  priceNumber: number;
  // Código da moeda (ex.: BRL)
  price_currency_code: string;
  // Período da assinatura (ex.: P1M, P1Y)
  subscriptionPeriod: string;
}

// IDs dos produtos no RevenueCat
export const PRODUCT_IDS = {
  PREMIUM_MONTHLY: '1mensal:mensalbasico',
  PREMIUM_YEARLY: '1anual:anualbasico'
} as const;

// Cache para os produtos
let isInitialized = false;
let isInitializing = false;

// Flag global para evitar múltiplas inicializações
let globalInitializationPromise: Promise<void> | null = null;
let isGloballyInitialized = false;

export class RevenueCatService {
  private static instance: RevenueCatService;
  private initialized = false;

  private constructor() {}

  static getInstance(): RevenueCatService {
    if (!RevenueCatService.instance) {
      RevenueCatService.instance = new RevenueCatService();
    }
    return RevenueCatService.instance;
  }

  private async initialize() {
    // Usar inicialização global para evitar múltiplas configurações
    if (isGloballyInitialized) {
      this.initialized = true;
      return;
    }
    
    if (globalInitializationPromise) {
      await globalInitializationPromise;
      this.initialized = true;
      return;
    }

    globalInitializationPromise = (async () => {
      try {
        const platform = Capacitor.getPlatform();
        const apiKey = platform === 'ios' ? REVENUECAT_API_KEY_IOS : REVENUECAT_API_KEY_ANDROID;

        if (!apiKey) {
          throw new Error('RevenueCat API key não encontrada para a plataforma atual');
        }

        // Obter o ID do usuário atual
        const userId = await this.getUserId();

        // Configurar o RevenueCat com o ID do usuário APENAS UMA VEZ
        await Purchases.configure({
          apiKey,
          appUserID: userId,
        });
        
        isGloballyInitialized = true;
        this.initialized = true;
      } catch (error) {
        // Reset flags em caso de erro para permitir nova tentativa
        globalInitializationPromise = null;
        isGloballyInitialized = false;
        throw error;
      }
    })();

    await globalInitializationPromise;
  }

  private async getUserId(): Promise<string | null> {
    const { data: { user } } = await supabase.auth.getUser();
    return user?.id || null;
  }

  async syncSubscriptionStatus() {
    try {
      await this.ensureInitialized();
      
      const { customerInfo } = await Purchases.getCustomerInfo();
      const userId = await this.getUserId();
      
      if (!userId) {
        return;
      }

      const isSubscribed = (customerInfo as CustomerInfo).entitlements.active['premium'] !== undefined;

      // Atualizar status da assinatura
      await this.updateSubscriptionStatus(userId, isSubscribed ? 'active' : 'expired');
      
      // Atualizar tipo de conta do usuário
      await this.updateUserAccountType(userId, isSubscribed ? 'premium' : 'free');
      
      // Forçar atualização do usuário no contexto de autenticação
      const { data: userData } = await supabase
          .from('users')
        .select('*')
        .eq('id', userId)
        .single();
        
      if (userData) {
        // Disparar evento para atualizar o contexto de autenticação
        window.dispatchEvent(new CustomEvent('user-updated', { detail: userData }));
      }
    } catch (error) {
      console.error('[RevenueCat] Erro ao sincronizar status da assinatura:', error);
    }
  }

  private async updateSubscriptionStatus(userId: string, status: 'active' | 'expired'): Promise<void> {
    try {
      await supabase
        .from('subscriptions')
        .update({ status })
        .eq('user_id', userId);
    } catch (error) {
      console.error('[RevenueCat] Erro ao atualizar status da assinatura:', error);
    }
  }

  private async ensureInitialized() {
    if (!this.initialized) {
      await this.initialize();
    }
  }

  async getProducts(): Promise<RevenueCatProduct[]> {
    try {
      if (!Capacitor.isNativePlatform()) {
        return [];
      }

      await this.ensureInitialized();

      const offerings = await Purchases.getOfferings();
      
      if (!offerings.current) {
        return [];
      }

      const products = offerings.current.availablePackages
        .map(pkg => ({
          id: pkg.product.identifier,
          identifier: pkg.identifier,
          price: pkg.product.priceString,
          priceNumber: Number(pkg.product.price) || 0,
          title: pkg.product.title,
          description: pkg.product.description,
          price_currency_code: pkg.product.currencyCode || 'BRL',
          subscriptionPeriod: (pkg.product.subscriptionPeriod as string) || 'unknown'
        }));

      return products;
    } catch (error) {
      console.error('[RevenueCat] Erro ao buscar produtos:', error);
      return [];
    }
  }

  private async updateUserAccountType(userId: string, type: 'free' | 'premium'): Promise<void> {
    try {
      const { error } = await supabase
        .from('users')
        .update({ tipo_de_conta: type })
        .eq('id', userId);

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('[RevenueCat] Erro ao atualizar tipo de conta:', error);
      throw error;
    }
  }

  async purchaseProduct(productId: string) {
    try {
      await this.ensureInitialized();

      const offerings = await Purchases.getOfferings();
      const currentOffering = offerings.current;

      if (!currentOffering) {
        throw new Error('Nenhuma oferta disponível');
      }

      const packageToPurchase = currentOffering.availablePackages.find(
          pkg => pkg.product.identifier === productId
        );

      if (!packageToPurchase) {
        throw new Error(`Produto não encontrado: ${productId}`);
      }

      const { customerInfo } = await Purchases.purchasePackage({ 
        aPackage: packageToPurchase 
      });

      // ✅ Enviar evento de compra para o Facebook SDK
      if (Capacitor.isNativePlatform()) {
        try {
          const price = packageToPurchase.product.price;
          const currency = packageToPurchase.product.currencyCode;
          await FacebookAnalytics.logPurchase({ amount: price, currency });
        } catch (e) {
          console.error('Erro ao enviar evento de compra para o Facebook', e);
        }
      }

      // Atualizar o tipo de conta do usuário para premium
      const userId = await this.getUserId();
      if (!userId) {
        throw new Error('Usuário não encontrado ao tentar atualizar tipo de conta');
      }

      // Atualizar status da assinatura e tipo de conta
      await Promise.all([
        this.updateSubscriptionStatus(userId, 'active'),
        this.updateUserAccountType(userId, 'premium')
      ]);

      // Forçar atualização do usuário no contexto de autenticação
      const { data: userData } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();
        
      if (userData) {
        // Disparar evento para atualizar o contexto de autenticação
        window.dispatchEvent(new CustomEvent('user-updated', { detail: userData }));
      }

      return customerInfo as CustomerInfo;
    } catch (error) {
      console.error('[RevenueCat] Erro ao realizar compra:', error);
      throw error;
    }
  }

  async restorePurchases() {
    try {
      await this.ensureInitialized();
      const customerInfo = await Purchases.restorePurchases();
      return customerInfo;
    } catch (error) {
      throw error;
    }
  }

  async getCustomerInfo() {
    try {
      await this.ensureInitialized();
      const customerInfo = await Purchases.getCustomerInfo();
      return customerInfo;
    } catch (error) {
      throw error;
    }
  }

  addPurchaseListener(callback: (info: CustomerInfo) => void) {
    Purchases.addCustomerInfoUpdateListener(callback);
  }
}

export default RevenueCatService.getInstance(); 